import { createContext, useCallback, useEffect, useReducer, useRef } from "react";
import axios from "app/utils/axios.js";
import { jwtDecode } from "jwt-decode";
import { MatxLoading } from "app/components";
import apiConstants from "app/utils/apiPaths";
import localStorageService from "app/service/localStorageService";
import { createSafeTimeoutHandle } from "app/utils/safeTimeout";

const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false
};

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  const decoded = jwtDecode(accessToken);
  return decoded.exp > Date.now() / 1000;
};

const sessionRefreshTimer = createSafeTimeoutHandle();

const setSession = (accessToken, refreshToken = null, logoutCallback) => {
  localStorageService.setToken({ access_token: accessToken, refreshToken });

  try {
    const { exp } = jwtDecode(accessToken);
    const timeout = exp * 1000 - Date.now();

    if (timeout > 0 && logoutCallback) {
      sessionRefreshTimer.schedule(() => {
        console.warn("Token expired — attempting refresh...");
        logoutCallback(); // Will call refresh handler or logout
      }, timeout - 5000); // refresh 5 seconds early
    } else {
      sessionRefreshTimer.clear();
    }
  } catch (err) {
    console.error("Failed to decode JWT for timer setup", err);
  }
};

const persistAppsCode = (user) => {
  const code = user?.appsCode;
  if (code && String(code).trim()) {
    localStorageService.setItem("appsCode", String(code).trim());
  }
  if (user?.tenantSlug) {
    try {
      const prev = JSON.parse(localStorage.getItem("tenantContext") || "{}");
      localStorage.setItem(
        "tenantContext",
        JSON.stringify({
          ...prev,
          tenantSlug: user.tenantSlug,
          appsCode: code || prev.appsCode,
          hospitalName: user.companyName || prev.hospitalName,
          tenantUrl: user.tenantUrl || prev.tenantUrl
        })
      );
    } catch {

      // ignore
    }}
};

const clearSession = () => {
  localStorageService.clearToken();
};

const getUserDetails = () => axios.get(apiConstants.USER_DETAILS);

/**
 * Guard AuthContext.user updates: never replace a logged-in Principal with
 * HTML/strings/partial payloads (e.g. SPA index.html from wrong axios baseURL).
 */
const validatePrincipalForUpdate = (candidate, { requireAppsCode }) => {
  if (candidate == null) {
    return { ok: false, reason: "null or undefined" };
  }
  if (typeof candidate === "string") {
    const looksHtml = /^\s*</.test(candidate) || /<!doctype|html/i.test(candidate);
    return {
      ok: false,
      reason: looksHtml ? "HTML string" : "string (not a Principal object)"
    };
  }
  if (Array.isArray(candidate)) {
    return { ok: false, reason: "array" };
  }
  if (typeof candidate !== "object") {
    return { ok: false, reason: `type ${typeof candidate}` };
  }
  if (!candidate.userId || !String(candidate.userId).trim()) {
    return { ok: false, reason: "missing userId" };
  }
  if (!Array.isArray(candidate.roleList)) {
    return { ok: false, reason: "missing roleList" };
  }
  if (requireAppsCode) {
    const code = candidate.appsCode;
    if (!code || !String(code).trim()) {
      return { ok: false, reason: "missing appsCode" };
    }
  }
  return { ok: true };
};

const sameRoleList = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

/**
 * Skip UPDATE_USER when identity / setup / profile fields are unchanged.
 * Percent and section progress must participate so banners update after save.
 */
const isSamePrincipalForUpdate = (prev, next) => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return (
    prev.userId === next.userId &&
    prev.appsCode === next.appsCode &&
    prev.userAccnId === next.userAccnId &&
    prev.hospitalSetupStatus === next.hospitalSetupStatus &&
    Boolean(prev.hospitalSetupComplete) === Boolean(next.hospitalSetupComplete) &&
    (prev.hospitalSetupPercentComplete ?? 0) === (
    next.hospitalSetupPercentComplete ?? 0) &&
    prev.onboardingStatus === next.onboardingStatus &&
    Boolean(prev.onboardingComplete) === Boolean(next.onboardingComplete) &&
    (prev.onboardingPercentComplete ?? 0) === (next.onboardingPercentComplete ?? 0) &&
    sameOnboardingSections(prev.onboardingSections, next.onboardingSections) &&
    prev.defaultApplication === next.defaultApplication &&
    sameRoleList(prev.roleList, next.roleList) &&
    prev.userName === next.userName &&
    prev.userPhoneNumber === next.userPhoneNumber &&
    prev.profileImage === next.profileImage &&
    prev.companyName === next.companyName &&
    prev.accountStatus === next.accountStatus &&
    prev.logoUrl === next.logoUrl &&
    prev.faviconUrl === next.faviconUrl &&
    Boolean(prev.hasLogo) === Boolean(next.hasLogo) &&
    Boolean(prev.hasFavicon) === Boolean(next.hasFavicon) &&
    sameTheme(prev.theme, next.theme));

};

const sameTheme = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.primaryColor === b.primaryColor && a.secondaryColor === b.secondaryColor;
};

const sameOnboardingSections = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) && !Array.isArray(b)) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (
    a[i]?.id !== b[i]?.id ||
    Boolean(a[i]?.complete) !== Boolean(b[i]?.complete))
    {
      return false;
    }
  }
  return true;
};

const tryDispatchUserUpdate = (dispatch, candidate, requireAppsCode, previousUser) => {
  const validation = validatePrincipalForUpdate(candidate, { requireAppsCode });
  if (!validation.ok) {
    console.warn(
      `updateUser rejected: ${validation.reason}. Existing AuthContext.user left unchanged.`,
      typeof candidate === "string" ? candidate.slice(0, 120) : candidate
    );
    return false;
  }
  if (isSamePrincipalForUpdate(previousUser, candidate)) {
    return false;
  }
  dispatch({ type: "UPDATE_USER", payload: { user: candidate } });
  return true;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT":
      if (action.payload.user) {
        persistAppsCode(action.payload.user);
      }
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user
      };
    case "LOGIN":
      localStorageService.setItem("user", action.payload.user.empName);
      persistAppsCode(action.payload.user);
      return { ...state, isAuthenticated: true, user: action.payload.user };
    case "LOGOUT":
      clearSession();
      return { ...state, isAuthenticated: false, user: null };
    case "REGISTER":
      // Registration submits signup data only; it does not establish a login session.
      return {
        ...state,
        isAuthenticated: false,
        accnRegistration: action.payload.accnRegistration
      };
    case "UPDATE_USER":
      persistAppsCode(action.payload.user);
      return { ...state, user: action.payload.user };
    default:
      return state;
  }
};

const assertTenantHostMatchesUser = (user) => {
  try {
    const hostSlug = (window.location.hostname || "").toLowerCase().replace(/\.$/, "");
    const base = (window._env_?.TENANT_BASE_DOMAIN || "cheritto.com").toLowerCase();
    if (!hostSlug.endsWith(`.${base}`)) return;
    const label = hostSlug.slice(0, -(base.length + 1));
    if (!label || label.includes(".") || ["app", "uat-app", "www"].includes(label)) return;
    const ctxRaw = localStorage.getItem("tenantContext");
    const ctx = ctxRaw ? JSON.parse(ctxRaw) : null;
    if (ctx?.appsCode && user?.appsCode && String(ctx.appsCode) !== String(user.appsCode)) {
      throw new Error("This account is not authorized for this hospital URL.");
    }
  } catch (e) {
    if (e?.message?.includes("not authorized")) throw e;
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  verifyOTP: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  updateUser: () => {}
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const logout = () => {
    sessionRefreshTimer.clear();
    axios
    .get(apiConstants.LOGOUT)
    .catch(() => {})
    .finally(() => {
      clearSession();
      dispatch({ type: "LOGOUT" });
    });
  };

  const updateUser = useCallback((updatedUser) => {
    const current = stateRef.current;
    tryDispatchUserUpdate(
      dispatch,
      updatedUser,
      Boolean(current.isAuthenticated),
      current.user
    );
  }, []);

  const refreshTokenHandler = async () => {
    try {
      const refreshToken = localStorageService.getRefreshToken();
      if (!refreshToken) throw new Error("No refresh token");

      const response = await axios.post(apiConstants.REFREST_TOKEN, {
        refreshToken
      });

      const { jwt: newToken, refreshToken: newRefreshToken } =
      response.data;
      setSession(newToken, newRefreshToken, refreshTokenHandler); // Reset timer

      const userResponse = await getUserDetails();
      tryDispatchUserUpdate(
        dispatch,
        userResponse.data,
        true,
        stateRef.current.user
      );

      console.info("Token successfully refreshed");
    } catch (error) {
      console.error("Token refresh failed. Logging out.");
      logout();
    }
  };

  const login = async (username, password, rememberMe) => {
    const response = await axios.post(apiConstants.LOGIN_TOKEN, {
      username,
      password,
      rememberMe
    });

    const data = response.data;

    if (data.temptoken) {
      localStorage.setItem("tempToken", data.temptoken);
      localStorage.setItem("authPhase", "2FA");
      return { status: "2FA_REQUIRED" };
    }

    const { jwt, refreshToken } = data;
    setSession(jwt, refreshToken, refreshTokenHandler);

    const userResponse = await getUserDetails();
    const user = userResponse.data;
    try {
      assertTenantHostMatchesUser(user);
    } catch (err) {
      clearSession();
      throw err;
    }

    dispatch({
      type: "LOGIN",
      payload: { token: { jwt, refreshToken }, user }
    });
    return { status: "LOGGED_IN", user };
  };

  const verifyOTP = async (otp) => {
    const tempToken = localStorage.getItem("tempToken");
    if (!tempToken) throw new Error("Missing temporary token");

    const decoded = jwtDecode(tempToken);
    const username = decoded.sub;

    const response = await axios.post(
      apiConstants.VERIFY_2FA,
      { username, code: parseInt(otp, 10) },
      {
        headers: { Authorization: `Bearer ${tempToken}` }
      }
    );

    const { jwt, refreshToken } = response.data;

    if (!jwt || !refreshToken) {
      return { status: "FAILED" };
    }

    localStorage.removeItem("tempToken");
    localStorage.removeItem("authPhase");

    setSession(jwt, refreshToken, refreshTokenHandler);

    const userResponse = await getUserDetails();
    const user = userResponse.data;
    try {
      assertTenantHostMatchesUser(user);
    } catch (err) {
      clearSession();
      throw err;
    }

    dispatch({
      type: "LOGIN",
      payload: { token: { jwt, refreshToken }, user }
    });
    return { status: "LOGGED_IN", user };
  };

  const register = async (
  firstNameOrPayload,
  lastName,
  workEmailId,
  companyName,
  companySize) =>
  {
    const payload =
    firstNameOrPayload &&
    typeof firstNameOrPayload === "object" &&
    !Array.isArray(firstNameOrPayload) ?
    firstNameOrPayload :
    {
      firstName: firstNameOrPayload,
      lastName,
      workEmailId,
      companyName,
      companySize
    };

    const response = await axios.post(apiConstants.REGISTER_ACCOUNT, payload);
    const responseData = response.data;
    dispatch({ type: "REGISTER", payload: responseData });
    return responseData;
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken, null, refreshTokenHandler);

          const response = await getUserDetails();
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: true, user: response.data }
          });
        } else {
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: false, user: null }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: false, user: null }
        });
      }
    })();
    return () => {
      sessionRefreshTimer.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps (preserve behavior)
  }, []);

  if (!state.isInitialized) return <MatxLoading />;

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        verifyOTP,
        logout,
        register,
        updateUser
      }}>

      {children}
    </AuthContext.Provider>);

};

export default AuthContext;